import CodeEditor from "../common/code-editor";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";

import TextField from "@mui/material/TextField";
import LoadingSpinner from "../common/loading";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import DeletePremadeDialog from "./delete-premade-dialog";
import Chip from "@mui/material/Chip";
import {
  buildPremade,
  deletePremade,
  formatPremade,
  getPremade,
  publishPremade,
  savePremade,
} from "./premade-service";
import { useToast } from "../common/toast";
import { useTheme } from "@mui/material/styles";
import useApiCall from "../common/api-call";

const PremadeScriptView = ({
  premadeId: propPremadeId,
  version: propVersion,
  onNavigateBack,
  isInDialog = false,
}) => {
  const { successToast, warningToast, errorToast } = useToast();
  const history = useHistory();
  const [spinButton, setSpinButton] = useState(null);

  const [loading, setLoading] = useState(true);
  const [premade, setPremade] = useState(null);
  const [premadeId, setPremadeId] = useState(propPremadeId || null);
  const [version, setVersion] = useState(propVersion || "");
  const [code, setCode] = useState("");
  const [summary, setSummary] = useState("");
  const [parseResults, setParseResults] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const { apiCall } = useApiCall();
  const theme = useTheme();

  useEffect(() => {
    if (propPremadeId && propVersion) {
      // If props are provided (dialog mode), use them
      loadPremade({ id: propPremadeId, version: propVersion });
    } else {
      // Otherwise, read from URL parameters (standalone mode)
      const searchParams = new URLSearchParams(window.location.search);
      const params = {};
      for (const [key, value] of searchParams.entries()) {
        params[key] = value;
      }
      if (params["id"]) {
        loadPremade(params);
      } else {
        setPremade({ version: "draft" });
        setVersion("draft");
        setLoading(false);
      }
    }
  }, [propPremadeId, propVersion]);

  const loadPremade = async params => {
    setLoading(true);
    const premade = await getPremade(apiCall, params["id"], params["version"]);
    if (premade && !premade.error) {
      setPremade(premade);
      setPremadeId(premade.premadeId);
      setVersion(premade.version);
      setCode(premade.script);
      setParseResults(premade.parseResults);
      setSummary(premade.summary);
      if (!isInDialog) {
        history.replace(
          `/premade/script?id=${premade.premadeId}&version=${premade.version}`,
        );
      }
    } else {
      setPremade({ version: "draft" });
      errorToast("Error!");
    }
    setLoading(false);
  };

  const handleSave = async () => {
    // Validation
    if (!summary.trim()) {
      errorToast("Summary is required!");
      return;
    }
    if (!code.trim()) {
      errorToast("Script is required!");
      return;
    }

    setSpinButton("save");

    const resp = await savePremade(apiCall, premadeId, code, summary);
    if (resp != null && !resp.error) {
      successToast("Saved!");
      loadPremade({ id: resp.premadeId, version: resp.version });
    } else {
      errorToast("Error!");
    }
    setSpinButton(null);
  };

  const handlePublish = async () => {
    setSpinButton("publish");
    const resp = await publishPremade(apiCall, premadeId);
    if (resp) {
      successToast("Published!");
      await loadPremade({ id: premadeId, version: "prod" });
    } else {
      errorToast("Publish Failed!");
    }
    setSpinButton(null);
  };

  const handleBuild = async () => {
    if (!code.trim()) {
      errorToast("Script is required for build!");
      return;
    }

    const resp = await buildPremade(apiCall, code);
    if (resp && !resp.error) {
      setParseResults(resp.parseResults);
      if (resp.parseResults.length > 0) {
        warningToast("Build Issues Found");
      } else {
        successToast("Build Successful");
      }
    } else {
      errorToast("Build Error!");
    }
  };

  const handleFormat = async () => {
    if (!code.trim()) {
      errorToast("Script is required for format!");
      return;
    }

    const resp = await formatPremade(apiCall, code);
    if (resp) {
      setParseResults(resp.parseResults);
      if (resp.parseResults.length === 0) {
        successToast("Format Successful");
        setCode(resp.script);
      } else {
        warningToast("Format Issues Found");
      }
    } else {
      errorToast("Format Error!");
    }
  };

  const handleDelete = async () => {
    const success = await deletePremade(apiCall, premadeId, version);
    if (success) {
      if (isInDialog && onNavigateBack) {
        onNavigateBack();
      } else {
        history.replace("/premade/manage");
      }
    } else {
      errorToast("Error. Please retry.");
    }
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Box
          sx={{
            flexGrow: 1,
            height: isInDialog ? "calc(90vh - 120px)" : "calc(100vh - 64px)",
            padding: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              height: "100%",
            }}
          >
            {/* Action buttons bar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Chip
                label={version}
                color={
                  version === "draft"
                    ? "warning"
                    : version === "stg"
                    ? "secondary"
                    : "primary"
                }
              />
              <Button variant="outlined" onClick={handleBuild}>
                Build
              </Button>
              <Button variant="outlined" onClick={handleFormat}>
                Format
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={spinButton !== null}
              >
                Save
                {spinButton === "save" && (
                  <CircularProgress
                    size={20}
                    style={{
                      marginLeft: 10,
                      color: theme.palette.background.c0,
                    }}
                  />
                )}
              </Button>
              {premade.version === "draft" ||
                (premade.version === "stg" && (
                  <Button
                    variant="contained"
                    onClick={handlePublish}
                    disabled={
                      (version !== "draft" && version !== "stg") ||
                      !premadeId ||
                      spinButton !== null
                    }
                  >
                    Publish
                    {spinButton === "publish" && (
                      <CircularProgress
                        size={20}
                        style={{
                          marginLeft: 10,
                          color: theme.palette.background.c0,
                        }}
                      />
                    )}
                  </Button>
                ))}
              {premade.version !== "draft" && (
                <IconButton
                  aria-label="delete"
                  onClick={() => setDeleteConfirm(true)}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>

            {/* Summary input */}
            <Box>
              <TextField
                fullWidth
                label="Summary"
                placeholder="Enter a brief description of this premade..."
                value={summary}
                onChange={e => {
                  setSummary(e.target.value);
                }}
                variant="outlined"
                multiline
                rows={2}
              />
            </Box>

            {/* Code editor */}
            <Box sx={{ flex: 1, height: "100%" }}>
              <CodeEditor
                value={code}
                editable={true}
                parseResults={parseResults}
                height={
                  isInDialog ? "calc(90vh - 320px)" : "calc(100vh - 200px)"
                }
                onChange={instance => {
                  const code = instance.getValue();
                  setCode(code);
                }}
              />
            </Box>
          </Box>

          <DeletePremadeDialog
            open={deleteConfirm}
            onClose={() => setDeleteConfirm(false)}
            onDelete={handleDelete}
          ></DeletePremadeDialog>
        </Box>
      )}
    </>
  );
};

export default PremadeScriptView;
